import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import Badge from './common/Badge';
import './Layout.css';

const Layout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeModule, setActiveModule] = useState('communications');
  const [moduleBadges, setModuleBadges] = useState({});
  
  // ===== Online Status States =====
  const [isOnline, setIsOnline] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // ===== Language =====
  const { lang, toggleLang, t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const sl = t('sidebar');
  
  const navigate = useNavigate();
  const location = useLocation();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ===== Fetch agent online status on mount =====
  useEffect(() => {
    const fetchOnlineStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch('/api/v1/agents/me/status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            setIsOnline(data.is_online === 1);
          }
        }
      } catch (err) {
        console.error('Error fetching online status:', err);
      }
    };
    fetchOnlineStatus();
  }, []);

  // ===== Activity-based Heartbeat =====
  useEffect(() => {
    if (!isOnline) return;
    
    let lastActivity = Date.now();
    
    const sendHeartbeat = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        const inactiveMinutes = (Date.now() - lastActivity) / 60000;
        if (inactiveMinutes > 15) return;
        try {
            await fetch('/api/v1/agents/me/heartbeat', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
        } catch (err) {
            console.error('Heartbeat failed:', err);
        }
    };

    const trackActivity = () => { lastActivity = Date.now(); };
    
    document.addEventListener('mousemove', trackActivity);
    document.addEventListener('mousedown', trackActivity);
    document.addEventListener('keydown', trackActivity);
    document.addEventListener('scroll', trackActivity);
    document.addEventListener('touchstart', trackActivity);

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 5 * 60 * 1000);
    
    const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
            trackActivity();
            sendHeartbeat();
        }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibility);
        document.removeEventListener('mousemove', trackActivity);
        document.removeEventListener('mousedown', trackActivity);
        document.removeEventListener('keydown', trackActivity);
        document.removeEventListener('scroll', trackActivity);
        document.removeEventListener('touchstart', trackActivity);
    };
  }, [isOnline]);

  // ===== Toggle Online/Offline =====
  const toggleOnlineStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token || statusLoading) return;
    
    setStatusLoading(true);
    try {
      const newStatus = !isOnline;
      const res = await fetch('/api/v1/agents/me/status', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_online: newStatus })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setIsOnline(newStatus);
        }
      }
    } catch (err) {
      console.error('Error toggling status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (storedUser.menu_permissions && typeof storedUser.menu_permissions === 'string') {
      try {
        storedUser.menu_permissions = JSON.parse(storedUser.menu_permissions);
      } catch (e) {
        console.error('Error parsing menu_permissions:', e);
        storedUser.menu_permissions = [];
      }
    }
    setUser(storedUser);
  }, []);

  // Get org modules from user object
  const orgModules = user.org_modules || {};
  const isModuleEnabled = (mod) => !mod || !orgModules[mod] || orgModules[mod].enabled;

  const hasPermission = (permissionKey) => {
    if (!permissionKey) return true;
    if (user.role === 'admin' && !user.menu_permissions) return true;
    const permissions = user.menu_permissions || [];
    if (permissions.length === 0 && user.role === 'admin') return true;
    if (permissionKey === 'all_conversations' && user.can_view_all_conversations) return true;
    return Array.isArray(permissions) && permissions.includes(permissionKey);
  };

  // ============================================================
  // MODULE DEFINITIONS — each module has main items + settings
  // ============================================================
  const modules = useMemo(() => [
    {
      id: 'communications',
      label: sl.communications || 'Communications',
      label_short: sl.comms_short || 'Comms',
      icon: '💬',
      module_key: 'communications',
      main: [
        { id: 'dashboard', permissionKey: 'dashboard', label: sl.dashboard || 'Dashboard', icon: '📊', path: '/dashboard' },
        { id: 'all-conversations', permissionKey: 'all_conversations', label: sl.all_conversations || 'Conversations', icon: '💬', path: '/all-conversations' },
        { id: 'leads', permissionKey: 'leads', label: sl.leads || 'Leads', icon: '🛒', path: '/leads' },
        { id: 'marketing', permissionKey: 'marketing', label: sl.marketing || 'Marketing', icon: '📢', path: '/marketing', roles: ['supervisor', 'admin'] },
        { id: 'booking-calendar', permissionKey: 'booking_calendar', label: sl.booking_calendar || 'Book Meeting', icon: '🗓️', path: '/booking-calendar' },
        { id: 'my-bookings', permissionKey: 'my_bookings', label: sl.my_bookings || 'My Bookings', icon: '📅', path: '/bookings' },
        { id: 'negative-feedback', permissionKey: 'negative_feedback', label: sl.negative_feedback || 'Bad Reviews', icon: '⚠️', path: '/negative-feedback' },
        { id: 'analytics', permissionKey: 'analytics', label: sl.analytics || 'Analytics', icon: '📈', path: '/analytics' },
      ],
      settings: [
        { id: 'canned-responses', permissionKey: 'canned_responses', label: sl.canned_responses || 'Canned Responses', icon: '💾', path: '/canned-responses' },
        { id: 'event-types', permissionKey: 'event_types', label: sl.event_types || 'Event Types', icon: '📅', path: '/event-types' },
        { id: 'availability', permissionKey: 'availability', label: sl.availability || 'Availability', icon: '🕐', path: '/availability' },
      ],
    },
    {
      id: 'helpdesk',
      label: sl.helpdesk_module || 'Helpdesk',
      label_short: sl.helpdesk_short || 'Helpdesk',
      icon: '🎫',
      module_key: 'helpdesk',
      main: [
        { id: 'helpdesk', permissionKey: 'helpdesk', label: sl.helpdesk || 'Tickets', icon: '🎫', path: '/helpdesk' },
        { id: 'helpdesk-clients', permissionKey: 'helpdesk_clients', label: sl.clients || 'Clients', icon: '👥', path: '/helpdesk/clients', roles: ['admin', 'supervisor'] },
      ],
      settings: [
        { id: 'helpdesk-departments', permissionKey: 'helpdesk_departments', label: sl.departments || 'Departments', icon: '🏢', path: '/helpdesk/departments' },
      ],
    },
    {
      id: 'field_service',
      label: sl.field_service || 'Field Service',
      label_short: sl.fsm_short || 'FSM',
      icon: '🔧',
      module_key: 'field_service',
      main: [
        { id: 'fsm-dashboard', permissionKey: 'fsm_dashboard', label: sl.fsm_dashboard || 'Dashboard', icon: '📊', path: '/fsm/dashboard' },
        { id: 'fsm-equipment', permissionKey: 'fsm_equipment', label: sl.fsm_equipment || 'Equipment', icon: '🔧', path: '/fsm/equipment' },
        { id: 'fsm-clients', permissionKey: 'fsm_clients', label: sl.fsm_clients || 'Clients', icon: '🏭', path: '/fsm/clients' },
        { id: 'fsm-contracts', permissionKey: 'fsm_contracts', label: sl.fsm_contracts || 'Contracts', icon: '📄', path: '/fsm/contracts' },
        { id: 'fsm-work-orders', permissionKey: 'fsm_work_orders', label: sl.fsm_work_orders || 'Work Orders', icon: '🔧', path: '/fsm/work-orders' },
        { id: 'fsm-technicians', permissionKey: 'fsm_technicians', label: sl.fsm_technicians || 'Technicians', icon: '👷', path: '/fsm/technicians' },
        { id: 'fsm-dispatch', permissionKey: 'fsm_dispatch', label: sl.fsm_dispatch || 'Dispatch Board', icon: '📋', path: '/fsm/dispatch' },
        { id: 'fsm-reports', permissionKey: 'fsm_reports', label: sl.fsm_reports || 'Reports', icon: '📊', path: '/fsm/reports' },
        { id: 'fsm-maintenance-plans', permissionKey: 'fsm_maintenance_plans', label: sl.fsm_maintenance_plans || 'Maintenance Plans', icon: '🛠️', path: '/fsm/maintenance-plans' },
      ],
      settings: [
        { id: 'fsm-categories', permissionKey: 'fsm_categories', label: sl.fsm_categories || 'Categories', icon: '📋', path: '/fsm/categories', roles: ['admin', 'supervisor'] },
        { id: 'fsm-contract-templates', permissionKey: 'fsm_contract_templates', label: sl.fsm_contract_templates || 'Contract Templates', icon: '📑', path: '/fsm/contract-templates', roles: ['admin', 'supervisor'] },
        { id: 'fsm-wo-templates', permissionKey: 'fsm_wo_templates', label: sl.fsm_wo_templates || 'WO Templates', icon: '🔧', path: '/fsm/wo-templates', roles: ['admin', 'supervisor'] },
        { id: 'fsm-parts-catalog', permissionKey: 'fsm_parts_catalog', label: sl.fsm_parts_catalog || 'Parts Catalog', icon: '📦', path: '/fsm/parts-catalog', roles: ['admin', 'supervisor'] },
        { id: 'fsm-price-books', permissionKey: 'fsm_price_books', label: sl.fsm_price_books || 'Price Books', icon: '💰', path: '/fsm/price-books', roles: ['admin', 'supervisor'] },
        { id: 'fsm-business-hours', permissionKey: 'fsm_business_hours', label: sl.fsm_business_hours || 'Business Hours', icon: '🕐', path: '/fsm/business-hours', roles: ['admin', 'supervisor'] },
        { id: 'fsm-settings', permissionKey: 'fsm_settings', label: sl.fsm_settings || 'اعدادات FSM', icon: '⚙️', path: '/fsm/settings', roles: ['admin'] },
        
      ],
    },
    {
  id: 'vehicle_service',
  label: sl.vehicle_service || 'خدمات السيارات',
  label_short: sl.vsm_short || 'VSM',
  icon: '🚗',
  module_key: 'vehicle_service',
  main: [
    { id: 'vsm-clients', permissionKey: 'vsm_clients', label: sl.vsm_clients || 'العملاء', icon: '👥', path: '/vsm/clients' },
    { id: 'vsm-vehicles', permissionKey: 'vsm_vehicles', label: sl.vsm_vehicles || 'السيارات', icon: '🚙', path: '/vsm/vehicles' },
    { id: 'vsm-service-types', permissionKey: 'vsm_service_types', label: sl.vsm_service_types || 'أنواع الخدمات', icon: '🧰', path: '/vsm/service-types' },
    { id: 'vsm-contracts', permissionKey: 'vsm_contracts', label: sl.vsm_contracts || 'العقود', icon: '📋', path: '/vsm/contracts' },
    { id: 'vsm-schedule', permissionKey: 'vsm_schedule', label: sl.vsm_schedule || 'الجدول اليومي', icon: '📅', path: '/vsm/schedule' },
    { id: 'vsm-capture', permissionKey: 'vsm_capture', label: sl.vsm_capture || 'تنفيذ مهمة', icon: '📸', path: '/vsm/capture' },
  ],
  settings: [
    { id: 'vsm-holidays', permissionKey: 'vsm_holidays', label: sl.vsm_holidays || 'الإجازات وأيام العطلة', icon: '🌴', path: '/vsm/holidays', roles: ['admin', 'supervisor'] },
  ],
},
    
    {
      id: 'admin',
      label: sl.admin || 'Admin',
      label_short: sl.admin_short || 'Admin',
      icon: '⚙️',
      module_key: null, // always visible
      main: [
        { id: 'agents', permissionKey: 'agents', label: sl.agents || 'Agents', icon: '👥', path: '/agents' },
    { id: 'teams', permissionKey: 'teams', label: sl.teams || 'Teams', icon: '👨‍👩‍👧', path: '/teams' },
        { id: 'organizations', label: sl.organizations || 'Organizations', icon: '🏢', path: '/organizations', adminOnly: true },
      ],
      settings: [
        { id: 'my-profile', permissionKey: 'my_profile', label: sl.my_profile || 'My Profile', icon: '👤', path: '/agent-profile' },
      ],
    },
  ], [sl, user]);

  // ============================================================
  // Auto-detect active module from URL
  // ============================================================
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/fsm/')) setActiveModule('field_service');
    else if (path.startsWith('/vsm/')) setActiveModule('vehicle_service');
    else if (path.startsWith('/helpdesk')) setActiveModule('helpdesk');
    else if (path.startsWith('/agents') || path.startsWith('/organizations') || path.startsWith('/agent-profile') || path.startsWith('/teams')) setActiveModule('admin');
    else setActiveModule('communications');
  }, [location.pathname]);
  // ===== Fetch module badge counts =====
  useEffect(() => {
    const fetchBadges = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { 'Authorization': `Bearer ${token}` };
      const badges = {};
      try {
        // Communications: unassigned conversations
        const convRes = await fetch('/api/v1/conversations/stats', { headers });
        if (convRes.ok) {
          const convData = await convRes.json();
          badges.communications = convData.unassigned || convData.active || 0;
        }
      } catch (e) {}
      try {
        // Helpdesk: open tickets count
        const hdRes = await fetch('/api/v1/helpdesk-agent/dashboard-stats', { headers });
        if (hdRes.ok) {
          const hdData = await hdRes.json();
          const stats = hdData.stats || hdData;
          badges.helpdesk = stats.open || stats.active || 0;
        }
      } catch (e) {}
      try {
        // FSM: active contracts or equipment
        const fsmRes = await fetch('/api/v1/fsm/contracts/stats', { headers });
        if (fsmRes.ok) {
          const fsmData = await fsmRes.json();
          const d = fsmData.data || fsmData;
          badges.field_service = d.expiring_30d || 0;
        }
      } catch (e) {}
      setModuleBadges(badges);
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);


  // ============================================================
  // Filter items based on permissions/roles
  // ============================================================
  const filterItems = (items) => {
    return (items || []).filter(item => {
      if (item.adminOnly && user.role !== "admin") return false;
      if (item.roles && !item.roles.includes(user.role)) return false;
      if (item.id === 'helpdesk-clients' && !['admin', 'supervisor'].includes(user.role)) return false;
      // customCheck is the sole gate (e.g. leads → can_view_leads, marketing → allow_marketing)
      if (item.customCheck) return item.customCheck();
      return hasPermission(item.permissionKey);
    });
  };

  // Get visible modules (org-level + user-level filtering)
  // A module is visible only if:
  //   - The org has it enabled (organization_modules)
  //   - The user has access to its content:
  //     * For agents: at least one MAIN item is visible (settings are hidden for agents)
  //     * For admin/supervisor: at least one main OR settings item is visible
  const isPrivileged = user.role === 'admin' || user.role === 'supervisor';
  const visibleModules = modules.filter(m => {
    if (!isModuleEnabled(m.module_key)) return false;
    const visibleMain = filterItems(m.main);
    if (isPrivileged) {
      const visibleSettings = filterItems(m.settings);
      return (visibleMain.length + visibleSettings.length) > 0;
    }
    return visibleMain.length > 0;
  });

  // Get current module content. If activeModule is no longer visible, fall back to first visible.
  let currentModule = visibleModules.find(m => m.id === activeModule);
  if (!currentModule) currentModule = visibleModules[0] || modules[0];
  const mainItems = filterItems(currentModule.main);
  const settingsItems = filterItems(currentModule.settings);

  // ============================================================
  // Handlers (preserved from original)
  // ============================================================
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch('/api/v1/agents/me/status', {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_online: false })
        }).catch(() => {});

        await fetch('/api/v1/auth/logout', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const handleMenuClick = (item) => {
    if (item.path) {
      navigate(item.path);
      if (isMobile) {
        setMobileMenuOpen(false);
      }
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleOverlayClick = () => {
    setMobileMenuOpen(false);
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className={`layout ${lang === 'ar' ? 'layout-rtl' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Mobile Header with Hamburger */}
      <header className="mobile-header">
        <button 
          className="hamburger-btn"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
        <div className="mobile-brand">
          <span className="brand-icon">🤖</span>
          <span className="brand-text">MatixERP Agent</span>
        </div>
        
        <button
          onClick={toggleLang}
          style={{
            background: 'rgba(99,102,241,0.15)',
            border: '1px solid rgba(99,102,241,0.3)',
            color: '#818cf8',
            borderRadius: '6px',
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: '700',
            cursor: 'pointer',
            marginRight: '8px',
          }}
        >
          🌐 {lang === 'ar' ? 'EN' : 'عربي'}
        </button>
      </header>

      {/* Overlay for mobile */}
      <div 
        className={`sidebar-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={handleOverlayClick}
      ></div>

      {/* ===== SIDEBAR ===== */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Top: Brand + Close/Collapse */}
        <div className="sidebar-header">
          <div className="brand">
            <span className="brand-icon">🤖</span>
            {!sidebarCollapsed && <span className="brand-text">MatixERP Agent</span>}
          </div>
          <button
            className="mobile-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
          <button
            className="btn btn-ghost btn-icon desktop-collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        {/* Middle: Module content (scrollable) */}
        <nav className="sidebar-nav" style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>

          {/* Module Title */}
          {!sidebarCollapsed && (
            <div style={{
              padding: '12px 16px 8px',
              fontSize: '10px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              color: 'rgba(148, 163, 184, 0.7)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span style={{ fontSize: '14px' }}>{currentModule.icon}</span>
              {currentModule.label}
            </div>
          )}

          {/* Main Items */}
          {mainItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => handleMenuClick(item)}
              title={sidebarCollapsed ? item.label : ''}
            >
              <span className="nav-icon">{item.icon}</span>
              {!sidebarCollapsed && (
                <>
                  <span className="nav-label">{item.label}</span>
                  {item.badge > 0 && (
                    <Badge variant="error" size="sm">{item.badge}</Badge>
                  )}
                </>
              )}
            </button>
          ))}

          {/* Settings / Lookup Tables Divider — visible only to admins/supervisors */}
          {settingsItems.length > 0 && (user.role === 'admin' || user.role === 'supervisor') && (
            <>
              {!sidebarCollapsed ? (
                <div style={{
                  padding: '14px 16px 6px',
                  fontSize: '10px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1.2px',
                  color: 'rgba(148, 163, 184, 0.5)',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <span style={{ fontSize: '12px' }}>⚙️</span>
                  {sl.settings_lookup || 'Settings'}
                </div>
              ) : (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '8px 12px' }} />
              )}

              {settingsItems.map(item => (
                <button
                  key={item.id}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
                </button>
              ))}
            </>
          )}

          {/* Help Guide Link */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '8px 12px' }} />
          <button
            className="nav-item"
            onClick={() => window.open('/help/matixfsm-guide.html', '_blank')}
            title={sidebarCollapsed ? (lang === 'ar' ? 'دليل المستخدم' : 'Help Guide') : ''}
            style={{ color: '#60a5fa' }}
          >
            <span className="nav-icon">📖</span>
            {!sidebarCollapsed && <span className="nav-label">{lang === 'ar' ? 'دليل المستخدم' : 'Help Guide'}</span>}
          </button>
        </nav>

        {/* ===== BOTTOM SECTION (fixed) ===== */}
        <div className="sidebar-footer">

          {/* ===== Module Dock (Option A - Pill tabs with badge dots) ===== */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: sidebarCollapsed ? 4 : 2,
            padding: sidebarCollapsed ? '10px 8px' : '10px 4px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            marginBottom: 8,
          }}>
            {visibleModules.map(m => {
              const isActiveM = activeModule === m.id;
              const badgeCount = moduleBadges[m.id] || 0;
              return (
                <button
                  key={m.id}
                  onClick={() => {
                    setActiveModule(m.id);
                    const firstItem = filterItems(m.main)[0];
                    if (firstItem) navigate(firstItem.path);
                  }}
                  title={m.label + (badgeCount ? ` (${badgeCount})` : '')}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    padding: sidebarCollapsed ? '8px 6px' : '8px 0',
                    borderRadius: 10,
                    border: 'none',
                    cursor: 'pointer',
                    background: isActiveM ? 'rgba(99,102,241,0.2)' : 'transparent',
                    transition: 'all 0.2s',
                    flex: sidebarCollapsed ? 'none' : 1,
                    minWidth: 0,
                    position: 'relative',
                  }}
                  onMouseEnter={e => { if (!isActiveM) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isActiveM ? 'rgba(99,102,241,0.2)' : 'transparent'; }}
                >
                  <span style={{
                    fontSize: sidebarCollapsed ? 18 : 20,
                    lineHeight: 1,
                    filter: isActiveM ? 'none' : 'grayscale(0.5)',
                    transition: 'filter 0.2s',
                  }}>{m.icon}</span>
                  {!sidebarCollapsed && (
                    <span style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: isActiveM ? '#a5b4fc' : '#94a3b8',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>{m.label_short}</span>
                  )}
                  {isActiveM && (
                    <div style={{
                      width: sidebarCollapsed ? 14 : 20,
                      height: 2,
                      borderRadius: 1,
                      background: '#818cf8',
                      marginTop: 1,
                    }} />
                  )}
                  {badgeCount > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 2,
                      right: sidebarCollapsed ? 2 : 8,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#ef4444',
                      border: '2px solid #12182f',
                      boxShadow: '0 0 6px rgba(239,68,68,0.5)',
                    }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* ===== Quick Controls Row ===== */}
          <div style={{
            display: 'flex',
            gap: 6,
            padding: '0 8px',
            marginBottom: 8,
            justifyContent: sidebarCollapsed ? 'center' : 'stretch',
          }}>
            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              title={lang === 'ar' ? 'Switch to English' : 'التحويل للعربي'}
              style={{
                flex: sidebarCollapsed ? 'none' : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '8px',
                borderRadius: 8,
                cursor: 'pointer',
                background: 'rgba(99,102,241,0.1)',
                border: '1px solid rgba(99,102,241,0.25)',
                color: '#818cf8',
                fontSize: 12,
                fontWeight: 600,
                transition: 'all 0.2s',
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.2)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.1)'; }}
            >
              🌐 {!sidebarCollapsed && (lang === 'ar' ? 'EN' : 'عربي')}
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? 'Switch to Light' : 'Switch to Dark'}
              style={{
                flex: sidebarCollapsed ? 'none' : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '8px',
                borderRadius: 8,
                cursor: 'pointer',
                background: isDark ? 'rgba(251,191,36,0.1)' : 'rgba(99,102,241,0.1)',
                border: `1px solid ${isDark ? 'rgba(251,191,36,0.25)' : 'rgba(99,102,241,0.25)'}`,
                color: isDark ? '#fbbf24' : '#818cf8',
                fontSize: 12,
                fontWeight: 600,
                transition: 'all 0.2s',
              }}
              onMouseOver={e => { e.currentTarget.style.background = isDark ? 'rgba(251,191,36,0.2)' : 'rgba(99,102,241,0.2)'; }}
              onMouseOut={e => { e.currentTarget.style.background = isDark ? 'rgba(251,191,36,0.1)' : 'rgba(99,102,241,0.1)'; }}
            >
              {isDark ? '☀️' : '🌙'} {!sidebarCollapsed && (isDark ? 'Light' : 'Dark')}
            </button>
          </div>

          {/* ===== Online/Offline Toggle ===== */}
          <div 
            className="online-status-toggle"
            onClick={toggleOnlineStatus}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 12px',
              margin: '0 8px 8px',
              borderRadius: '8px',
              cursor: statusLoading ? 'wait' : 'pointer',
              background: isOnline 
                ? 'rgba(34, 197, 94, 0.1)' 
                : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${isOnline 
                ? 'rgba(34, 197, 94, 0.3)' 
                : 'rgba(239, 68, 68, 0.3)'}`,
              transition: 'all 0.3s ease',
              opacity: statusLoading ? 0.6 : 1,
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            }}
          >
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: isOnline ? '#22c55e' : '#ef4444',
              boxShadow: isOnline 
                ? '0 0 8px rgba(34, 197, 94, 0.6)' 
                : '0 0 8px rgba(239, 68, 68, 0.4)',
              flexShrink: 0,
              transition: 'all 0.3s ease',
            }} />
            
            {!sidebarCollapsed && (
              <span style={{
                flex: 1,
                fontSize: '13px',
                fontWeight: '500',
                color: isOnline ? '#22c55e' : '#ef4444',
                transition: 'color 0.3s ease',
              }}>
                {isOnline ? (sl.online || 'Online') : (sl.offline || 'Offline')}
              </span>
            )}
            
            {!sidebarCollapsed && (
              <div style={{
                width: '36px',
                height: '20px',
                borderRadius: '10px',
                background: isOnline ? '#22c55e' : '#4b5563',
                position: 'relative',
                transition: 'background 0.3s ease',
                flexShrink: 0,
              }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: isOnline ? '18px' : '2px',
                  transition: 'left 0.3s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }} />
              </div>
            )}
          </div>

          {/* ===== User Profile + Logout ===== */}
          <div style={{ padding: '0 8px 8px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.03)',
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  backgroundColor: '#4a5568',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                  color: 'white',
                }}>
                  {(user.full_name || 'A').charAt(0).toUpperCase()}
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: -1,
                  right: -1,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: isOnline ? '#22c55e' : '#ef4444',
                  border: '2px solid #12182f',
                }} />
              </div>
              {!sidebarCollapsed && (
                <>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: '#f9fafb',
                      fontSize: 13,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {user.full_name || 'Agent'}
                    </div>
                    <div><Badge variant="info" size="sm">{user.role}</Badge></div>
                  </div>
                  <button
                    onClick={handleLogout}
                    title={sl.logout || 'Logout'}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#9ca3af',
                      fontSize: 16,
                      padding: 4,
                      borderRadius: 4,
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                  >
                    🚪
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <div className="content-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;